import SeoHelmet from "@/components/UI/SeoHelmet";
import logo from "@/public/img/logo.png";
import seoImg from "@/public/img/seo/ueber-uns.png";
import footerSrc from "@/public/svg/obst-bg.svg";
import { FaFacebookF, FaInstagram, FaWhatsapp, FaMailBulk, FaMapMarkerAlt, FaStar, FaTruck } from "react-icons/fa";
import { EventData, track } from "@/helpers/analytics";
import { FACEBOOK_URL, INSTAGRAM_URL, NEWSLETTER_URL, WHATSAPP_CHANNEL_URL, WHATSAPP_CHAT_URL } from "@/data/contact";

const socialLinks: { title: string; description: string; href: string; event: string; eventData?: EventData; badge: React.ReactNode }[] = [
  {
    title: "Hilfe beim Einkauf",
    description: "Schreib uns auf WhatsApp und nenne bitte deinen Standort.",
    href: WHATSAPP_CHAT_URL,
    event: "whatsapp-click",
    badge: <FaWhatsapp aria-hidden="true" />,
  },
  {
    title: "Feedback oder Produktwunsch",
    description: "Sag uns, was gut läuft und was wir in deinem Yobsti verbessern können.",
    href: "https://yobsti.de/fuer-kunden#feedback",
    event: "cta-click",
    eventData: { target: "feedback" },
    badge: <FaStar aria-hidden="true" />,
  },
  {
    title: "Selbst liefern oder Anbieter empfehlen",
    description: "Du stellst Lebensmittel her oder kennst einen guten Anbieter aus der Region?",
    href: "https://yobsti.de/fuer-lieferanten",
    event: "cta-click",
    eventData: { target: "suppliers" },
    badge: <FaTruck aria-hidden="true" />,
  },
  {
    title: "Standort und Öffnungszeiten",
    description: "Finde deinen Yobsti und informiere dich über Öffnungszeiten und Zugang.",
    href: "https://yobsti.de/standorte",
    event: "cta-click",
    eventData: { target: "find-store" },
    badge: <FaMapMarkerAlt aria-hidden="true" />,
  },
  {
    title: "Neuigkeiten abonnieren",
    description: "Unser WhatsApp-Kanal mit Neuigkeiten, Angeboten und Informationen aus unseren Märkten.",
    href: WHATSAPP_CHANNEL_URL,
    event: "whatsapp-channel-click",
    badge: <FaWhatsapp aria-hidden="true" />,
  },
  {
    title: "Yobsti auf Instagram",
    description: "Neue Produkte und Einblicke aus der Region.",
    href: INSTAGRAM_URL,
    event: "social-click",
    eventData: { network: "instagram" },
    badge: <FaInstagram aria-hidden="true" />,
  },
  {
    title: "Yobsti auf Facebook",
    description: "Neuigkeiten, Angebote und Einblicke aus der Region.",
    href: FACEBOOK_URL,
    event: "social-click",
    eventData: { network: "facebook" },
    badge: <FaFacebookF aria-hidden="true" />,
  },
  {
    title: "Yobsti-Newsletter",
    description: "Aktionen und Neuigkeiten direkt per Mail.",
    href: NEWSLETTER_URL,
    event: "social-click",
    eventData: { network: "newsletter" },
    badge: <FaMailBulk aria-hidden="true" />,
  },
  {
    title: "Zur Yobsti-Webseite",
    description: "Sortiment, Standorte und alles rund um deinen Yobsti.",
    href: "https://yobsti.de",
    event: "cta-click",
    eventData: { target: "website" },
    badge: "Web",
  },
];

export default function SocialLinksPage() {
  return (
    <section
      className="w-screen min-h-screen bg-cover bg-center relative flex flex-col"
      style={{ backgroundImage: `url(${footerSrc})` }}
    >
      <SeoHelmet
        title="Yobsti Links – alle Wege zu uns"
        description="Die Social-Media- und Web-Links von Yobsti auf einer Seite."
        image={seoImg}
        url="https://www.yobsti.de/links"
      />

      <div className="relative mx-auto flex min-h-screen w-full max-w-4xl flex-col items-center px-6 pb-16 pt-20 font-nunito text-textColor">
        <div className="absolute left-0 top-10 h-40 w-40 rounded-full bg-primaryLight/60 blur-3xl" />
        <div className="absolute right-10 top-28 h-48 w-48 rounded-full bg-secondaryLight/70 blur-3xl" />
        <div className="absolute bottom-10 left-1/2 h-56 w-56 -translate-x-1/2 rounded-full bg-tertiaryLight/80 blur-3xl" />

        <div className="relative flex w-full flex-col items-center text-center">
          <div className="fade-up relative mb-6 flex items-center justify-center">
            <div className="absolute -inset-6 rounded-full bg-white/70 blur-2xl" />
            <img
              src={logo}
              alt="Yobsti Logo"
              className="relative h-32 rounded-full border border-white/70 bg-white/80 p-2 shadow-lg"
            />
          </div>
          <h1 className="fade-up fade-up-delay-2 mt-4 text-3xl font-bold sm:text-4xl">
            Wie können wir dir helfen?
          </h1>
          <p className="fade-up fade-up-delay-3 mt-3 max-w-2xl text-sm sm:text-base">
            Hier findest du den passenden Weg zu uns.
          </p>
        </div>

        <div className="fade-up fade-up-delay-1 mt-10 flex w-full flex-col gap-5">
          {socialLinks.map((link) => (
            <a
              key={link.title}
              href={link.href}
              target="_blank"
              rel="noreferrer"
              onClick={() => track(link.event, { ...link.eventData, location: "links" })}
              className="group relative overflow-hidden rounded-3xl border border-white/60 bg-white/70 p-5 shadow-[0_14px_40px_-30px_rgba(0,0,0,0.35)] transition hover:bg-white/90"
            >
              <div className="absolute right-6 top-6 h-16 w-16 rounded-full bg-white/40 blur-2xl transition group-hover:scale-125" />
              <div className="relative flex items-center gap-4">
                <div
                  className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br bg-primary text-2xl font-bold text-white shadow-md"
                >
                  {link.badge}
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold sm:text-xl">
                    {link.title}
                  </h2>
                  <p className="mt-1 text-sm text-textColor/70">
                    {link.description}
                  </p>
                </div>
                <span className="text-sm font-semibold uppercase tracking-widest text-primary">
                  öffnen
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
